import { useState } from "react";
import api, { API, apiError, getToken } from "@/lib/api";
import { rupee } from "@/lib/constants";
import { toast } from "sonner";
import { Download, Copy, Receipt, Check, Loader2, Pencil, X } from "lucide-react";
import { VendorForm } from "@/components/admin/VendorList";

const fileSrc = (p) => `${API}/files/${p}?auth=${getToken()}`;
const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };

function BankEditor({ v, onDone, onClose }) {
    const [approve, setApprove] = useState(v.status !== "APPROVED");
    const save = async (f) => {
        try {
            await api.patch(`/admin/vendors/${v.id}`, { ...f, ...(approve ? { status: "APPROVED" } : {}) });
            toast.success(approve ? "Bank details saved & vendor approved" : "Bank details saved"); onClose(); onDone();
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 p-4" onClick={onClose}>
            <div data-testid="vendor-bank-modal" className="w-full max-w-2xl rounded-2xl bg-white p-5" onClick={(e) => e.stopPropagation()}>
                <div className="mb-3 flex items-center justify-between"><h2 className="font-heading text-lg font-extrabold text-navy">{v.name} — bank & contact details</h2><button aria-label="Close" onClick={onClose}><X size={18} className="text-navy/50" /></button></div>
                {v.status !== "APPROVED" && <label className="mb-3 flex items-center gap-2 rounded-xl bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800"><input data-testid="vendor-bank-approve" type="checkbox" checked={approve} onChange={(e) => setApprove(e.target.checked)} className="accent-emerald-600" /> Approve this vendor when I save</label>}
                <VendorForm init={v} onSave={save} onCancel={onClose} />
            </div>
        </div>
    );
}

function Bank({ v, onDone }) {
    const [open, setOpen] = useState(false);
    if (!v) return <span className="text-xs text-rose-600">No vendor</span>;
    const editBtn = <button data-testid={`vendor-bank-edit-${v.id}`} onClick={() => setOpen(true)} className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold text-orange-dark hover:underline"><Pencil size={11} /> {v.has_bank ? "Edit bank details" : "Add bank details"}</button>;
    const modal = open && <BankEditor v={v} onDone={onDone} onClose={() => setOpen(false)} />;
    if (!v.has_bank) return <div className="min-w-[130px]"><span className="text-xs font-semibold text-amber-700">Bank details missing · {v.phone || "no mobile"}</span><br />{editBtn}{modal}</div>;
    const line = v.upi_id ? `UPI ${v.upi_id}` : `${v.account_name || ""} A/c ${v.account_number} · ${v.ifsc}`;
    return (
        <div className="min-w-[130px]">
            <button data-testid={`vendor-bank-copy-${v.id}`} onClick={() => copy(v.upi_id || `${v.account_number} ${v.ifsc}`)} className="flex items-start gap-1 text-left text-xs text-navy/70 hover:text-orange">
                <Copy size={12} className="mt-0.5 shrink-0" /><span>{line}{v.upi_id && v.account_number ? <><br />A/c {v.account_number} · {v.ifsc}</> : null}</span>
            </button>
            {editBtn}{modal}
        </div>
    );
}

function PayCell({ r, onDone }) {
    const [utr, setUtr] = useState("");
    const [busy, setBusy] = useState(false);
    if (r.status === "PAID") return <span className="text-xs font-bold text-emerald-700" data-testid={`vbill-paid-${r.booking_ref}`}><Check size={12} className="inline" /> Paid · {r.utr}</span>;
    const pay = async () => {
        if (!r.customer_paid && !window.confirm("The customer hasn't paid for these materials yet. Pay the vendor anyway?")) return;
        setBusy(true);
        try { await api.post(`/admin/vendor-bills/${r.id}/paid`, { utr }); toast.success("Marked paid"); onDone(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    return (
        <div className="flex gap-1">
            <input data-testid={`vbill-utr-${r.booking_ref}`} value={utr} onChange={(e) => setUtr(e.target.value)} placeholder="UTR" className="w-24 rounded-lg border border-navy/15 px-2 py-1 text-xs outline-none" />
            <button data-testid={`vbill-mark-paid-${r.booking_ref}`} disabled={busy} onClick={pay} className="rounded-full bg-navy px-2.5 py-1 text-xs font-bold text-white disabled:opacity-50">{busy ? <Loader2 size={12} className="animate-spin" /> : "Paid"}</button>
        </div>
    );
}

function VendorCell({ r, vendors, onDone }) {
    const link = async (vid) => {
        try { await api.post(`/admin/vendor-bills/${r.id}/vendor`, { vendor_id: vid }); toast.success("Vendor linked"); onDone(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    };
    return (
        <div className="min-w-[140px]">
            <p className="font-semibold text-navy">{r.vendor?.name || "—"}{r.vendor?.status === "PENDING" && <span className="ml-1 rounded bg-amber-50 px-1 text-[10px] font-bold text-amber-700">PENDING</span>}</p>
            <p className="text-[11px] text-navy/45">{r.vendor?.phone || ""}</p>
            {r.status !== "PAID" && (
                <select data-testid={`vbill-link-${r.booking_ref}`} value="" onChange={(e) => e.target.value && link(e.target.value)} className="mt-1 w-full rounded border border-navy/10 bg-white text-[11px] text-navy/60">
                    <option value="">Change vendor…</option>
                    {vendors.filter((v) => v.id !== r.vendor_id).map((v) => <option key={v.id} value={v.id}>{v.name} {v.phone ? `· ${v.phone}` : ""}</option>)}
                </select>
            )}
        </div>
    );
}

export function VendorBills({ rows, vendors, onChanged }) {
    const tot = (k, f = () => true) => rows.filter(f).reduce((t, r) => t + Number(r[k] || 0), 0);
    const cards = [["Bills", rows.length, false], ["Bill value", tot("bill_amount"), true], ["To pay vendors", tot("net_payable", (r) => r.status !== "PAID"), true],
        ["HOMIIE margin + GST", tot("margin") + tot("margin_gst"), true]];
    return (
        <>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {cards.map(([l, v, m]) => (
                    <div key={l} className="rounded-2xl border border-navy/10 bg-white p-4" data-testid={`vbill-stat-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                        <p className="text-xs font-semibold uppercase tracking-wide text-navy/45">{l}</p>
                        <p className="mt-1 font-heading text-xl font-extrabold text-navy">{m ? rupee(Math.round(v * 100) / 100) : v}</p>
                    </div>
                ))}
            </div>
            <div className="mt-4 overflow-x-auto rounded-2xl border border-navy/10 bg-white homiie-scroll">
                <table className="w-full text-sm" data-testid="vendor-bills-table">
                    <thead className="bg-navy/[0.03] text-left text-xs uppercase tracking-wide text-navy/50">
                        <tr>{["Booking", "Bill date", "Pro", "Vendor", "Bill", "Customer paid", "Invoice", "Margin 10% + GST", "Pay vendor", "Bank details", "Status", ""].map((h) => <th key={h} className="whitespace-nowrap px-3 py-3">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} className="border-t border-navy/5 align-top" data-testid={`vbill-row-${r.booking_ref}`}>
                                <td className="px-3 py-3 font-heading font-bold text-navy">{r.booking_ref}<div className="text-[11px] font-normal text-navy/45">{r.service_name}</div></td>
                                <td className="whitespace-nowrap px-3 py-3 text-navy/70">{r.bill_date}</td>
                                <td className="px-3 py-3 text-navy/70">{r.pro_name}<div className="text-[11px] text-navy/40">{r.pro_code}</div></td>
                                <td className="px-3 py-3"><VendorCell r={r} vendors={vendors} onDone={onChanged} /></td>
                                <td className="px-3 py-3 font-bold text-navy">{rupee(r.bill_amount)}</td>
                                <td className="px-3 py-3"><span className={`rounded-full px-2 py-0.5 text-xs font-bold ${r.customer_paid ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`} data-testid={`vbill-cust-paid-${r.booking_ref}`}>{r.customer_paid ? "Received" : "Not yet"}</span></td>
                                <td className="px-3 py-3">{r.file_path && (/\.pdf$/i.test(r.file_path) ? <a href={fileSrc(r.file_path)} target="_blank" rel="noreferrer" className="text-navy/50"><Receipt size={22} /></a> : <a href={fileSrc(r.file_path)} target="_blank" rel="noreferrer"><img src={fileSrc(r.file_path)} alt="Bill" className="h-11 w-11 rounded-lg object-cover" /></a>)}</td>
                                <td className="whitespace-nowrap px-3 py-3 text-rose-600">−{rupee(r.margin)}<div className="text-[11px]">GST −{rupee(r.margin_gst)}</div></td>
                                <td className="px-3 py-3 font-heading font-extrabold text-navy" data-testid={`vbill-net-${r.booking_ref}`}>{rupee(r.net_payable)}</td>
                                <td className="px-3 py-3"><Bank v={r.vendor} onDone={onChanged} /></td>
                                <td className="px-3 py-3"><PayCell r={r} onDone={onChanged} /></td>
                                <td className="px-3 py-3"><a data-testid={`vbill-margin-invoice-${r.booking_ref}`} href={`${API}/admin/vendor-bills/${r.id}/margin-invoice.pdf?auth=${getToken()}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-bold text-orange-dark"><Download size={13} /> Margin invoice</a></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {rows.length === 0 && <div className="py-12 text-center text-sm text-navy/40">No material bills yet.</div>}
            </div>
        </>
    );
}
